<template>
  <div>
    <div ref="inputRow" class="flex items-center px-3 py-2 md:py-2">
      <div class="flex items-center flex-grow min-w-0">
        <o-icon icon="search" class="text-gray-400 dark:text-gray-500 mr-2 flex-shrink-0" :size="14" />
        <input
          ref="search"
          v-model="query"
          type="text"
          :placeholder="$t('service.addService')"
          class="w-full py-1 text-sm bg-transparent placeholder:text-gray-400 focus:outline-none dark:placeholder:text-gray-500"
          @focus="handleFocus"
          @keydown.esc="handleEsc"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="selectFocused"
        >
        <button
          v-if="query"
          type="button"
          class="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          :title="$t('general.clear')"
          @mousedown.prevent="clearQuery"
        >
          <o-icon icon="x" :size="12" />
        </button>
      </div>
    </div>

    <o-dropdown
      v-if="anchor"
      ref="dropdown"
      :anchor="anchor"
      :arrow="false"
      placement="bottom-start"
      same-width
      default-class="z-10 border border-gray-200 bg-white shadow-lg rounded-lg dark:bg-gray-900 dark:border-gray-800"
      padding=""
      @shown="updateAvailableHeight"
      @hide="handleDropdownHide"
    >
      <template #content>
        <div ref="mobileSearch" class="sm:hidden flex items-center flex-grow min-w-0 py-3 px-4 border-b dark:border-gray-700">
          <o-icon icon="search" class="text-gray-400 dark:text-gray-500 mr-2 flex-shrink-0" :size="14" />
          <input
            v-model="query"
            type="text"
            :placeholder="$t('service.addService')"
            class="w-full py-1 text-sm bg-transparent placeholder:text-gray-400 focus:outline-none dark:placeholder:text-gray-500"
            @focus="handleFocus"
            @keydown.esc="handleEsc"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="selectFocused"
          >
          <button
            v-if="query"
            type="button"
            class="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            :title="$t('general.clear')"
            @mousedown.prevent="clearQuery"
          >
            <o-icon icon="x" :size="12" />
          </button>
        </div>

        <div v-if="presetEmployee" ref="presetEmployeeInfo" class="flex items-center justify-between gap-2 px-4 py-2 bg-blue-50 border-b text-xs text-blue-800 dark:bg-blue-900/5 dark:text-blue-100 dark:border-gray-800">
          <p class="min-w-0">
            <span class="font-medium">{{ presetEmployeeName }}</span>
            {{ $t('components.reservation.create.servicePicker.presetEmployeeInfo') }}
          </p>
          <o-button
            size="xs"
            flat
            icon="x"
            :title="$t('components.reservation.create.servicePicker.presetEmployeeClear')"
            @click="$emit('clear-preset-employee')"
          />
        </div>

        <o-scroll ref="serviceList" base-class="h-full w-full max-w-full" :style="serviceListStyle">
          <div v-if="!grouped.length" class="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
            {{ $t('components.fields.listBox.noOptions') }}
          </div>

          <div v-for="group in grouped" :key="group.idServiceCategory">
            <p v-if="group.label" class="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider mt-3 mb-1 px-4">
              {{ group.label }}
            </p>
            <ul>
              <template v-for="service in group.services">
                <o-tooltip
                  v-if="!isAllowed(service)"
                  :key="service.idService"
                  :text="$t('components.reservation.create.servicePicker.serviceNotEnabledForEmployee', { employee: presetEmployeeName })"
                >
                  <li
                    class="select-none flex items-center justify-between py-2 px-4 text-sm opacity-50 cursor-not-allowed"
                    @mousedown.prevent
                  >
                    <div class="flex items-center min-w-0">
                      <o-avatar :items="[service]" class="hidden md:flex pr-2 flex-shrink-0" />
                      <div class="min-w-0">
                        <p class="line-clamp-1">
                          {{ service.title }}
                        </p>
                        <p v-if="query && service.category?.name" class="text-xs opacity-70 line-clamp-1">
                          {{ service.category.name }}
                        </p>
                      </div>
                    </div>
                  </li>
                </o-tooltip>

                <li
                  v-else
                  :key="service.idService"
                  class="select-none flex items-center justify-between py-2 px-4 transition-colors text-sm"
                  :class="[serviceClass(service), isSelected(service) ? '' : 'cursor-pointer']"
                  @mousedown.prevent="!isSelected(service) && handleAdd(service)"
                >
                  <div class="flex items-center min-w-0">
                    <o-avatar :items="[service]" class="hidden md:flex pr-2 flex-shrink-0" />
                    <div class="min-w-0">
                      <p class="line-clamp-1">
                        {{ service.title }}
                      </p>
                      <p v-if="query && service.category?.name" class="text-xs opacity-70 line-clamp-1">
                        {{ service.category.name }}
                      </p>
                    </div>
                  </div>

                  <div
                    v-if="isSelected(service)"
                    class="ml-2 flex-shrink-0 flex items-center border rounded bg-white dark:bg-gray-900 dark:border-gray-700"
                  >
                    <button
                      class="w-6 h-6 flex items-center justify-center text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-l transition-colors"
                      @mousedown.prevent.stop="handleRemove(service)"
                    >
                      <o-icon icon="subtract" :size="10" />
                    </button>
                    <span class="min-w-[1.5rem] px-1 text-center text-xs font-medium text-primary-700 dark:text-primary-300">
                      {{ selectedCount(service) }}
                    </span>
                    <button
                      class="w-6 h-6 flex items-center justify-center text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-r transition-colors"
                      @mousedown.prevent.stop="handleAdd(service)"
                    >
                      <o-icon icon="add" :size="10" />
                    </button>
                  </div>

                  <div
                    v-else
                    class="ml-2 flex-shrink-0 w-6 h-6 rounded border border-dashed border-gray-300 dark:border-gray-800 flex items-center justify-center text-gray-400 dark:text-gray-500"
                  >
                    <o-icon icon="add" :size="10" />
                  </div>
                </li>
              </template>
            </ul>
          </div>
        </o-scroll>

        <div ref="footer" class="border-t px-4 py-2 flex items-center justify-between">
          <p class="text-xs text-gray-500 dark:text-gray-400">
            <span v-if="selectedIds.length">{{ $t('components.reservation.create.servicePicker.selectedCount', { count: selectedIds.length }, selectedIds.length) }}</span>
            <span v-if="selectedIds.length === 1" class="text-gray-500 dark:text-gray-400 px-2">|</span>
            <span v-if="selectedIds.length < 2">{{ $t('components.reservation.create.servicePicker.selectPrompt') }}</span>
          </p>

          <div>
            <o-button size="sm" variant="primary" @click="handleClose">
              {{ $t('general.done') }}
            </o-button>
          </div>
        </div>
      </template>
    </o-dropdown>
  </div>
</template>

<script>
import Fuse from 'fuse.js'

import {
  OAvatar,
} from '@/components/elements'
import { useServiceStore } from '~/stores/useServiceStore'

export default {
  components: {
    OAvatar,
  },
  props: {
    selectedIds: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: null,
      validator: value => ['single', 'group'].includes(value),
    },
    presetEmployee: {
      type: Object,
      default: null,
    },
  },
  data () {
    return {
      query: '',
      anchor: null,
      focusedIndex: -1,
      listMaxHeight: 480,
      layoutFrame: null,
    }
  },
  computed: {
    services () {
      return useServiceStore().all
    },
    available () {
      const type = this.type
      return this.services.filter((service) => {
        if (type === 'group' && !service.isServiceCustomerTypeGroup) return false
        if (type === 'single' && !service.isServiceCustomerTypeSingle) return false
        return true
      })
    },
    searched () {
      if (!this.query.length) {
        return this.available
      }
      const fuse = new Fuse(this.available, {
        keys: ['title', 'information', 'category.name'],
        distance: 200,
        threshold: 0.35,
      })
      return fuse.search(this.query).map(({ item }) => item)
    },
    grouped () {
      if (this.query.length) {
        return [{
          idServiceCategory: 0,
          label: '',
          order: 0,
          services: this.searched,
        }]
      }

      const groups = {}
      for (const service of this.searched) {
        const category = service.category
        const id = category?.idServiceCategory || 0
        if (!groups[id]) {
          groups[id] = {
            idServiceCategory: id,
            label: category ? category.name : '',
            order: category ? category.listOrder : 0,
            services: [],
          }
        }
        groups[id].services.push(service)
      }

      return Object.values(groups)
        .sort((a, b) => a.order - b.order)
        .map(group => ({
          ...group,
          services: group.services.slice().sort((a, b) => (a.listOrder || 0) - (b.listOrder || 0)),
        }))
    },
    flatServices () {
      return this.grouped
        .reduce((acc, group) => acc.concat(group.services), [])
        .filter(service => this.isAllowed(service))
    },
    focusedService () {
      const services = this.flatServices
      if (this.focusedIndex >= 0 && this.focusedIndex < services.length) {
        return services[this.focusedIndex]
      }
      return null
    },
    presetEmployeeName () {
      const employee = this.presetEmployee
      if (!employee) return ''
      return employee.displayName || employee.name || employee.firstName || ''
    },
    serviceListStyle () {
      return {
        maxHeight: `${this.listMaxHeight}px`,
      }
    },
  },
  watch: {
    query () {
      this.focusedIndex = -1
      this.scheduleLayout()
    },
    presetEmployee () {
      this.scheduleLayout()
    },
  },
  mounted () {
    this.anchor = this.$refs.inputRow
    window.addEventListener('resize', this.scheduleLayout)
    document.addEventListener('scroll', this.scheduleLayout, true)
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.scheduleLayout)
    document.removeEventListener('scroll', this.scheduleLayout, true)

    if (this.layoutFrame) {
      cancelAnimationFrame(this.layoutFrame)
    }
  },
  methods: {
    isSelected (service) {
      return this.selectedIds.includes(service.idService)
    },
    isAllowed (service) {
      const employee = this.presetEmployee
      if (!employee) return true
      const id = employee.idEmployee ?? employee.id
      if (id == null) return true
      return Array.isArray(service.employeeIDs) && service.employeeIDs.includes(id)
    },
    selectedCount (service) {
      const id = service.idService
      return this.selectedIds.filter(selectedId => selectedId === id).length
    },
    handleAdd (service) {
      this.$emit('add', service)
    },
    handleRemove (service) {
      this.$emit('remove', service)
    },
    handleClose () {
      this.$refs.dropdown?.close()
      this.query = ''
      this.focusedIndex = -1
      this.$emit('close')
    },
    handleFocus () {
      this.$refs.dropdown?.open()
      this.scheduleLayout()
    },
    handleEsc () {
      this.query = ''
      this.$refs.dropdown?.close()
      this.$refs.search?.blur()
    },
    clearQuery () {
      this.query = ''
      this.focusedIndex = -1
      this.$refs.search?.focus()
    },
    handleDropdownHide () {
      this.query = ''
      this.focusedIndex = -1
      this.listMaxHeight = 480
      this.$emit('close')
    },
    scheduleLayout () {
      if (!this.$refs.dropdown?.show || this.layoutFrame) {
        return
      }

      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = null
        this.updateAvailableHeight()
      })
    },
    updateAvailableHeight () {
      const anchor = this.$refs.inputRow
      const list = this.$refs.serviceList?.$el

      if (!anchor || !list) {
        return
      }

      const viewport = window.visualViewport
      const viewportTop = viewport?.offsetTop || 0
      const viewportHeight = viewport?.height || window.innerHeight
      const viewportBottom = viewportTop + viewportHeight
      const anchorRect = anchor.getBoundingClientRect()
      const chromeHeight = [this.$refs.mobileSearch, this.$refs.presetEmployeeInfo, this.$refs.footer]
        .filter(Boolean)
        .reduce((height, element) => height + element.offsetHeight, 0)
      const padding = 12
      const availableAbove = anchorRect.top - viewportTop - chromeHeight - padding
      const availableBelow = viewportBottom - anchorRect.bottom - chromeHeight - padding
      const availableHeight = this.$refs.dropdown?.isFixed
        ? viewportHeight - chromeHeight - (padding * 2)
        : Math.max(availableAbove, availableBelow)
      const maxHeight = Math.max(96, Math.min(480, Math.floor(availableHeight)))

      if (maxHeight === this.listMaxHeight) {
        return
      }

      this.listMaxHeight = maxHeight

      this.$nextTick(() => {
        this.$refs.serviceList?.update()
      })
    },
    move (direction) {
      const total = this.flatServices.length
      if (!total) return

      let next = this.focusedIndex + direction
      if (next < 0) next = total - 1
      if (next >= total) next = 0
      this.focusedIndex = next
    },
    selectFocused () {
      const services = this.flatServices
      if (this.focusedIndex >= 0 && this.focusedIndex < services.length) {
        this.handleAdd(services[this.focusedIndex])
      }
    },
    serviceClass (service) {
      const selected = this.isSelected(service)
      const focused = service === this.focusedService

      if (selected) {
        return 'bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-800/20 dark:text-primary-300 dark:hover:bg-primary-900/50'
      }
      if (focused) {
        return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100'
      }
      return 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-100'
    },
  },
}
</script>
